// SETTINGS V2 — Kontakty card (SETTINGS_V2_SP3 §7).
//
// Owns the contacts card body (table / phone cards / empty), the Radix
// DropdownMenu row actions (first settings/v2 consumer of
// @radix-ui/react-dropdown-menu), the permission gates (SEC-REQ-10), and the
// delete confirm dialog (SettingsV2ConfirmDialog). Selects its own slice state
// so CompanyPageV2 stays under 300 lines. Data slice reused unchanged.
//
// SEC-REQ-11/12 (set-as-default, WIRED): the default badge renders TOLERANTLY
// (contact.isDefault === true only — undefined until the Customers DTO ships →
// no badge, no layout shift); the set-default handler does NO optimistic flip —
// on fulfilled it re-dispatches getContacts() (server-authoritative), on
// rejected the slice sets error → the page's existing error toast fires.
//
// Styling: inline styles + scoped CSS-var tokens + .sv2-tbl / .sv2-menu sheet
// classes + the Radix portal scope-class trick (§7.4). No MUI/sx/dt/Tailwind
// authored here. SEC-REQ-2: no import-time side effects.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import Loader from "../../../../shared/components/Loader";
import { CustomerContact } from "../../../shared/models/customerContactsModels";
import {
  changeDefaultContact,
  deleteContact,
  getContacts,
  selectContact,
} from "../../customerContactsSlice";
import { closeDialog, openDialog } from "../settingsV2UiSlice";
import { SCard } from "../atoms";
import SettingsV2ConfirmDialog from "../dialogs/SettingsV2ConfirmDialog";

const ellipsis: React.CSSProperties = {
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};

// Initials mirror stringAvatar's children derivation (first letters of the
// first two words, else first char) without importing MUI Avatar.
const initialsOf = (name: string): string => {
  const n = (name || "").toUpperCase().trim();
  if (!n) return "?";
  const parts = n.split(" ").filter(Boolean);
  return parts.length > 1 ? `${parts[0].charAt(0)}${parts[1].charAt(0)}` : parts[0].charAt(0);
};

const ContactAvatar: React.FC<{ contact: CustomerContact; size: number }> = ({ contact, size }) =>
  contact.file?.content ? (
    <img
      src={contact.file.content}
      alt=""
      style={{ width: size, height: size, borderRadius: 999, objectFit: "cover", flexShrink: 0 }}
    />
  ) : (
    <div
      style={{
        width: size, height: size, borderRadius: 999, background: "var(--bg-soft)",
        color: "var(--ink-2)", display: "grid", placeItems: "center",
        fontSize: size <= 32 ? 11 : 13, fontWeight: 600, flexShrink: 0,
      }}
    >
      {initialsOf(contact.name)}
    </div>
  );

const CompanyContactsCardV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  const { contacts, contact, loading } = useAppSelector((s) => s.customerContacts);
  const openDialogId = useAppSelector((s) => s.settingsV2Ui.openDialog);
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);

  const handleAdd = () => {
    dispatch(selectContact(null));
    dispatch(openDialog("contact"));
  };
  const handleEdit = (item: CustomerContact) => {
    dispatch(selectContact(item));
    dispatch(openDialog("contact"));
  };
  const handleAskDelete = (item: CustomerContact) => {
    dispatch(selectContact(item));
    dispatch(openDialog("deleteContact"));
  };
  // SEC-REQ-12 — NO optimistic flip, fail-closed.
  const handleSetDefault = async (item: CustomerContact) => {
    const result = await dispatch(changeDefaultContact(item.id));
    if (changeDefaultContact.fulfilled.match(result)) {
      await dispatch(getContacts()); // server-authoritative refresh
    } // rejected → slice sets error → existing error toast; nothing flips
  };
  const handleConfirmDelete = async () => {
    if (!contact) return;
    await dispatch(deleteContact(contact.id));
    await dispatch(getContacts()); // unconditional (§7.5 parity)
    dispatch(closeDialog());
    dispatch(selectContact(null));
  };
  const handleCancelDelete = () => {
    dispatch(closeDialog());
    dispatch(selectContact(null));
  };

  const defaultBadge = (
    <span
      style={{
        fontFamily: "var(--font-mono)", fontSize: 9.5, textTransform: "uppercase",
        letterSpacing: "0.06em", padding: "1px 6px", borderRadius: 999,
        background: "var(--mod-accent-soft)", color: "var(--mod-accent)", flexShrink: 0,
      }}
    >
      {t("settings:defaultContactBadge")}
    </span>
  );

  const rowMenu = (item: CustomerContact) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="btn btn-ghost"
          aria-label={t("settings:actions")}
          style={{ width: 30, padding: 0, justifyContent: "center" }}
        >
          ⋮
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`settings-v2-root p-${theme} mod-nastavenia sv2-menu`}
          align="end"
          sideOffset={4}
        >
          <PermissionGuard module={Modules.Settings} level={PermissionLevel.Write}>
            <DropdownMenu.Item className="sv2-menu-item" onSelect={() => handleEdit(item)}>
              {tCommon("buttons.edit")}
            </DropdownMenu.Item>
          </PermissionGuard>
          {item.isDefault !== true && (
            <PermissionGuard module={Modules.Settings} level={PermissionLevel.Write}>
              <DropdownMenu.Item className="sv2-menu-item" onSelect={() => handleSetDefault(item)}>
                {t("settings:setAsDefaultContact")}
              </DropdownMenu.Item>
            </PermissionGuard>
          )}
          <PermissionGuard module={Modules.Settings} level={PermissionLevel.Admin}>
            <DropdownMenu.Item
              className="sv2-menu-item"
              onSelect={() => handleAskDelete(item)}
              style={{ color: "var(--red)" }}
            >
              {tCommon("buttons.delete")}
            </DropdownMenu.Item>
          </PermissionGuard>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const phoneCard = (item: CustomerContact, index: number) => (
    <div
      key={item.id}
      style={{
        background: "var(--card)", border: "1px solid var(--border)",
        borderLeft: "3px solid var(--mod-accent)", borderRadius: 8, padding: 12,
        width: "100%", boxSizing: "border-box", overflow: "hidden",
        animation: `sv2-card-reveal 0.4s ease-out ${index * 0.06}s both`,
      }}
    >
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: item.email || item.phoneNumber ? 8 : 0,
        }}
      >
        <ContactAvatar contact={item} size={36} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", ...ellipsis }}>
            {item.name}
          </div>
          {item.position && (
            <div style={{ fontSize: 11, color: "var(--muted)", ...ellipsis }}>{item.position}</div>
          )}
        </div>
      </div>
      {item.email && (
        <div style={{ fontSize: 12, color: "var(--ink-2)", fontFamily: "var(--font-mono)", ...ellipsis }}>
          {item.email}
        </div>
      )}
      {item.phoneNumber && (
        <div style={{ fontSize: 12, color: "var(--ink-2)", fontFamily: "var(--font-mono)", ...ellipsis }}>
          {item.phoneNumber}
        </div>
      )}
    </div>
  );

  const tableRow = (item: CustomerContact) => (
    <tr key={item.id}>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <ContactAvatar contact={item} size={32} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", ...ellipsis }}>
              {item.name}
            </span>
            {item.isDefault === true && defaultBadge}
          </div>
        </div>
      </td>
      <td>{item.position}</td>
      <td>{item.department}</td>
      <td style={{ fontFamily: "var(--font-mono)" }}>{item.email}</td>
      <td style={{ fontFamily: "var(--font-mono)" }}>{item.phoneNumber}</td>
      <td style={{ textAlign: "right" }}>{rowMenu(item)}</td>
    </tr>
  );

  let body: React.ReactNode;
  if (loading) {
    body = <Loader />;
  } else if (!contacts?.length) {
    body = (
      <div
        style={{
          border: "1px dashed var(--border)", borderRadius: 8, padding: "32px 20px",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 6, textAlign: "center",
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
          {t("settings:contactsEmptyTitle")}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{t("settings:contactsEmptyHint")}</div>
      </div>
    );
  } else if (isPhone) {
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", overflow: "hidden" }}>
        {contacts.map((item, index) => phoneCard(item, index))}
      </div>
    );
  } else {
    body = (
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table className="sv2-tbl">
          <thead>
            <tr>
              <th>{tCommon("forms.name")}</th>
              <th>{t("settings:position")}</th>
              <th>{t("settings:dept")}</th>
              <th>{tCommon("forms.email")}</th>
              <th>{tCommon("forms.phone")}</th>
              <th style={{ textAlign: "right" }}>{t("settings:actions")}</th>
            </tr>
          </thead>
          <tbody>{contacts.map((item) => tableRow(item))}</tbody>
        </table>
      </div>
    );
  }

  return (
    <SCard
      title={t("settings:contactsTab")}
      style={{ animation: "sv2-card-reveal 0.4s ease-out 0.12s both" }}
      action={
        <PermissionGuard module={Modules.Settings} level={PermissionLevel.Write}>
          <button type="button" className="btn" onClick={handleAdd}>
            ＋ {t("settings:addNewContact")}
          </button>
        </PermissionGuard>
      }
    >
      {body}

      <SettingsV2ConfirmDialog
        open={openDialogId === "deleteContact"}
        onClose={handleCancelDelete}
        title={t("settings:deleteContact")}
        text={t("settings:deleteContactText", { name: contact?.name ?? "" })}
        confirmLabel={t("settings:deleteContactConfirm")}
        cancelLabel={t("settings:deleteContactCancel")}
        danger
        onConfirm={handleConfirmDelete}
      />
    </SCard>
  );
};

export default CompanyContactsCardV2;
